import Icon from '@elements/Icon';
import Button from '@elements/buttons/Button';
import Address from '@elements/forms/Address';
import InputField from '@elements/forms/InputField';
import ListBox from '@elements/forms/ListBox';
import PhoneInputField from '@elements/forms/PhoneNumberInput';
import checkIfAddressesAreEqual from '@helpers/checkIfAddressesAreEqual';
import extractNumberSuffix from '@helpers/extractNumberSuffix';
import { handleValidation } from '@helpers/handleValidation';
import { objectIsEmpty } from '@helpers/objectIsEmpty';
import { formatPhoneNumber, getRegionForPhoneNumber } from '@helpers/phoneValidation';
import populateBillingAddressPayload from '@helpers/populateBillingAddressPayload';
import populateShippingAddressData from '@helpers/populateShippingAddressData';
import populateShippingAddressPayload from '@helpers/populateShippingAddressPayload';
import removeIdAndTypeFromAddress from '@helpers/removeIdAndTypeFromAddress';
import { vatNumberRegex } from '@helpers/vatNumberRegex';
import { yupResolver } from '@hookform/resolvers/yup';
import { getOrganizationData, updateAddress, updateOrganization } from '@providers/organization';
import Spinner from 'components/admin/Spinner';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { baseValidationSchema } from './validation';

export default function OrgnanizationBusinessSettingsModalForm({
  industries,
  organizationId,
  setShowModal,
  organization,
  setOrganization,
  setIndustry,
  industry,
  setUpdated,
  setError,
  phoneValue,
  setPhoneValue,
  billingAddress,
  setBillingAddress,
  shippingAddress,
  setShippingAddress,
  orgSubData,
  referenceNumber,
  setReferenceNumber,
  hasBillingAddress,
  hasShippingAddress,
}) {
  const [isValid, setIsValid] = useState({});
  const [industryValid, setIndustryValid] = useState(true);
  const [loading, setLoading] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState('NL');
  const [addressNotFound, setAddressNotFound] = useState(false);
  const [shippingAddressChecked, setShippingAddressChecked] = useState(false);
  const [stripeTaxEnum, setStripeTaxEnum] = useState('');

  const billingAddressRequired = hasShippingAddress && !hasBillingAddress;

  const vatNumberValidationSchema = Yup.object().shape({
    vatNumber: Yup.string()
      .required('BTW is verplicht')
      .test('is-valid-vat', 'Ongeldig BTW-nummer', function (vatNumber) {
        if (objectIsEmpty(billingAddress) || vatNumberRegex[billingAddress.country] === null) {
          return true;
        }
        return vatNumberRegex[billingAddress.country].test(vatNumber);
      }),
  });

  const validationSchema = baseValidationSchema.concat(vatNumberValidationSchema);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema), mode: 'onChange' });

  function handlePhoneChange(value) {
    if (value.length > 5) {
      setPhoneValue(value);
      setPhoneCountry(getRegionForPhoneNumber(value));
      setValue('organizationContactNumber', value, { shouldValidate: true });
    }
  }

  function handleValidationWrapper(e, error) {
    handleValidation(e, error, trigger, setIsValid);
  }

  function handleIndustryChange(choice) {
    setIndustry(choice.value);
    customIndustryValidation(choice.value);
  }

  function customIndustryValidation(value) {
    if (value === industries.choices[0].value) {
      setIndustryValid(false);
      setIsValid({ ...isValid, industry: false });
      return false;
    } else {
      setIndustryValid(true);
      setIsValid({ ...isValid, industry: true });
      return true;
    }
  }

  async function onSubmit(formData) {
    if (!customIndustryValidation(industry)) {
      return;
    }

    if (!organization) {
      return;
    }

    setLoading(true);
    let billingAddressPayload, shippingAddressPayload, shippingAddressResponse;
    if (hasBillingAddress) {
      billingAddressPayload = populateBillingAddressPayload(billingAddress, null, {
        organization_id: organizationId,
      });

      if (!billingAddressPayload.number_suffix) {
        billingAddressPayload.number_suffix = extractNumberSuffix(billingAddressPayload.mail_lines);
      }

      if (hasShippingAddress) {
        shippingAddressPayload = populateShippingAddressPayload(shippingAddress, null, {
          organization_id: organizationId,
        });

        if (!shippingAddressPayload.number_suffix) {
          shippingAddressPayload.number_suffix = extractNumberSuffix(
            shippingAddressPayload.mail_lines,
          );
        }
      }

      const billingAddressResponse = await updateAddress(organizationId, billingAddressPayload);

      if (shippingAddressPayload) {
        shippingAddressResponse = await updateAddress(organizationId, shippingAddressPayload);
      }

      if (billingAddressResponse?.status !== 200) {
        toast.error('Factuuradres kon niet worden bijgewerkt.', {
          position: 'top-right',
          autoClose: 3000,
          icon: <Icon iconName="exclamation" color="text-functional-error" />,
          closeButton: false,
          bodyClassName: 'font-primary text-functional-error',
        });
        return;
      }

      if (shippingAddressResponse && shippingAddressResponse?.status !== 200) {
        toast.error('Verzendadres kon niet worden bijgewerkt.', {
          position: 'top-right',
          autoClose: 3000,
          icon: <Icon iconName="exclamation" color="text-functional-error" />,
          closeButton: false,
          bodyClassName: 'font-primary text-functional-error',
        });
        return;
      }
    }

    const payload = {
      organization_name: formData.organizationName,
      branch_name: industry,
      chamber_of_commerce_number: formData.chamberOfCommerceNumber,
      vat_number: formData.vatNumber,
      branch_location: formData.branchLocation,
      branch_number: formData.branchNumber,
      contact_email: formData.organizationEmail,
      contact_phone_number: formatPhoneNumber(phoneValue),
      billing_email: formData.billingEmail,
      contract_holder_name: formData.contractHolder,
      contract_holder_email: formData.contractHolderEmail,
      reference_number: formData.referenceNumber,
    };

    if (stripeTaxEnum) {
      payload.stripe_tax_enum = stripeTaxEnum;
    }

    let response = await updateOrganization(organizationId, payload);
    if (response?.status === 200) {
      setReferenceNumber(response.data.reference_number);
      response = await getOrganizationData(organizationId);
      if (response?.status === 200) {
        setOrganization(response.data);
      }
      setUpdated(true);
      setShowModal(false);
    } else {
      setError(true);
    }
    setLoading(false);
  }

  function toggleShippingAddress(e) {
    const { checked } = e.target;
    setShippingAddressChecked(checked);
    if (checked && hasBillingAddress) {
      const shippingAddressData = populateShippingAddressData(billingAddress, null, {
        organization_id: organizationId,
      });
      if (!shippingAddressData.number_suffix) {
        shippingAddressData.number_suffix = extractNumberSuffix(shippingAddressData.mail_lines);
      }
      setShippingAddress(shippingAddressData);
    } else {
      setShippingAddress(null);
    }
  }

  function handleStripeTaxEnum(country = '') {
    switch (country) {
      case 'Andorra':
        setStripeTaxEnum('ad_nrt');
        break;
      case 'Aruba':
      case 'Caribisch Nederland':
      case 'Curaçao':
      case 'Monaco':
      case 'San Marino':
      case 'Sint Maarten':
      case 'Vaticaanstad':
        setStripeTaxEnum('other');
        break;
      case 'Noorwegen':
        setStripeTaxEnum('no_vat');
        break;
      case 'Verenigd Koninkrijk':
        setStripeTaxEnum('gb_vat');
        break;
      case 'Zwitserland':
        setStripeTaxEnum('ch_vat');
        break;
      default:
        setStripeTaxEnum('eu_vat');
    }
  }

  useEffect(() => {
    if (!objectIsEmpty(billingAddress)) {
      trigger('vatNumber');
      handleStripeTaxEnum(billingAddress.country);
    }
  }, [billingAddress]);

  useEffect(() => {
    if (hasBillingAddress && hasShippingAddress) {
      const bAddress = removeIdAndTypeFromAddress(billingAddress);
      const sAddress = removeIdAndTypeFromAddress(shippingAddress);
      const addressesAreEqual = checkIfAddressesAreEqual(bAddress, sAddress);
      if (addressesAreEqual && !shippingAddressChecked) {
        setShippingAddressChecked(addressesAreEqual);
      }
    }
  }, [hasBillingAddress, hasShippingAddress]);

  useEffect(() => {
    if (hasBillingAddress && 'postcode' in isValid) {
      setShippingAddressChecked(false);
    }
  }, [isValid]);

  return (
    <form className="flex flex-col gap-4 pt-4" onSubmit={handleSubmit(onSubmit)}>
      <div className="flex flex-col gap-6">
        <InputField
          id="organizationName"
          label="Bedrijfsnaam*"
          name="organizationName"
          type="text"
          register={register}
          errors={errors}
          message={errors.organizationName?.message}
          onBlur={(e) => {
            handleValidationWrapper(e, errors?.organizationName);
          }}
          isValid={isValid['organizationName']}
          defaultValue={organization?.organization_name || ''}
        />
        <ListBox
          choices={industries.choices}
          label={industries.label}
          onChange={handleIndustryChange}
          errors={!industryValid}
          isValid={isValid['industry']}
          value={industry}
        />
        {!industryValid && (
          <div className="font-primary text-sm font-light tracking-wide text-functional-error">
            Branche is verplicht
          </div>
        )}
        <InputField
          id="branchLocation"
          label="Vestigingslocatie"
          name="branchLocation"
          type="text"
          register={register}
          errors={errors}
          message={errors.branchLocation?.message}
          onBlur={(e) => {
            handleValidationWrapper(e, errors?.branchLocation);
          }}
          isValid={isValid['branchLocation']}
          defaultValue={organization?.branch_location || ''}
        />
        <InputField
          id="chamberOfCommerceNumber"
          label="KvK-nummer*"
          name="chamberOfCommerceNumber"
          type="text"
          register={register}
          errors={errors}
          message={errors.chamberOfCommerceNumber?.message}
          onBlur={(e) => {
            handleValidationWrapper(e, errors?.chamberOfCommerceNumber);
          }}
          isValid={isValid['chamberOfCommerceNumber']}
          defaultValue={organization?.chamber_of_commerce_number || ''}
        />
        <InputField
          id="branchNumber"
          label="Vestigingsnummer"
          name="branchNumber"
          type="text"
          register={register}
          errors={errors}
          message={errors.branchNumber?.message}
          onBlur={(e) => {
            handleValidationWrapper(e, errors?.branchNumber);
          }}
          isValid={isValid['branchNumber']}
          defaultValue={organization?.branch_number || ''}
        />
        <InputField
          id="vatNumber"
          label="BTW-nummer*"
          name="vatNumber"
          type="tel"
          register={register}
          errors={errors}
          message={errors.vatNumber?.message}
          onBlur={(e) => {
            handleValidationWrapper(e, errors?.vatNumber);
          }}
          isValid={isValid['vatNumber']}
          defaultValue={organization?.vat_number || ''}
        />
        <InputField
          id="organizationEmail"
          label="Organisatie e-mail*"
          name="organizationEmail"
          type="text"
          register={register}
          errors={errors}
          message={errors.organizationEmail?.message}
          isValid={!errors.organizationEmail}
          defaultValue={organization?.contact_email || ''}
        />
        <PhoneInputField
          id="organizationContactNumber"
          label="Organisatienummer*"
          name="organizationContactNumber"
          register={register}
          errors={errors}
          message={errors.organizationContactNumber?.message}
          isValid={!errors.organizationContactNumber}
          phoneInputProps={{
            country: phoneCountry,
            value: formatPhoneNumber(phoneValue),
            onChange: handlePhoneChange,
          }}
        />
        <InputField
          id="billingEmail"
          label="Facturatie e-mail*"
          name="billingEmail"
          type="tel"
          register={register}
          errors={errors}
          message={errors.billingEmail?.message}
          isValid={!errors.billingEmail}
          defaultValue={organization?.billing_email || ''}
        />
        <InputField
          id="contractHolder"
          label="Contracthouder*"
          name="contractHolder"
          type="tel"
          register={register}
          errors={errors}
          message={errors.contractHolder?.message}
          isValid={!errors.contractHolder}
          defaultValue={organization?.contract_holder_name || ''}
        />
        <InputField
          id="contractHolderEmail"
          label="Contracthouder e-mail*"
          name="contractHolderEmail"
          type="tel"
          register={register}
          errors={errors}
          message={errors.contractHolderEmail?.message}
          isValid={!errors.contractHolderEmail}
          defaultValue={organization?.contract_holder_email || ''}
        />
        <div className="my-2">
          <h2 className="mb-4 font-primary text-lg font-bold text-primary-dark">
            Referentienummer
          </h2>
          <InputField
            id="referenceNumber"
            label="Referentienummer"
            name="referenceNumber"
            type="text"
            register={register}
            errors={errors}
            message={errors.referenceNumber?.message}
            onBlur={(e) => {
              handleValidationWrapper(e, errors?.referenceNumber);
            }}
            isValid={isValid['referenceNumber']}
            defaultValue={referenceNumber || orgSubData?.subscription?.reference_number}
          />
        </div>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="pb-5 font-primary text-lg font-bold text-primary-dark">Factuuradres</h2>
            {billingAddressRequired && (
              <div className="mb-6 font-primary text-sm font-light tracking-wide text-functional-error">
                Factuuradres is verplicht
              </div>
            )}
            <Address
              isBillingAddress={true}
              address={billingAddress}
              setAddress={setBillingAddress}
              isValid={isValid}
              setIsValid={setIsValid}
              setAddressNotFound={setAddressNotFound}
            />
          </div>
          <div className="mt-4 flex items-center gap-2">
            <input
              id="shippingAddressCheckbox"
              name="shippingAddressCheckbox"
              className="accent-accent"
              type="checkbox"
              {...register('shippingAddressCheckbox')}
              onChange={toggleShippingAddress}
              checked={shippingAddressChecked}
              disabled={addressNotFound}
            />
            <div className="text-sm leading-5">Verzendadres is hetzelfde als factuuradres</div>
          </div>
          <div>
            <h2 className="pb-5 font-primary text-lg font-bold text-primary-dark">Verzendadres</h2>
            <Address
              isBillingAddress={false}
              address={shippingAddress}
              setAddress={setShippingAddress}
              isValid={isValid}
              setIsValid={setIsValid}
              setAddressNotFound={setAddressNotFound}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-4">
        {loading ? (
          <Spinner spinnerSize="size-10" text="Verzoek aan het verwerken" textSize="text-sm" />
        ) : (
          <>
            <Button
              label="Annuleren"
              type="button"
              buttonType="secondary"
              onClick={() => setShowModal(false)}
            />
            <Button
              label="Versturen"
              type="submit"
              buttonType="primary"
              disabled={addressNotFound || billingAddressRequired}
            />
          </>
        )}
      </div>
    </form>
  );
}
